import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CircleAlert, Plus, RotateCw, Trash2 } from 'lucide-react';
import Loader from '@/components/ui/Loader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { StripePriceOptionInput } from '@/features/webinar/types';
import {
  formatStripeConnectedPriceLabel,
  mergeStripePrices,
  type StripeConnectedPrice,
} from '@/lib/stripeConnect';
import {
  INSTALLMENT_COUNT_MAX,
  INSTALLMENT_COUNT_MIN,
  STRIPE_PRICE_OPTIONS_MAX,
} from '@/lib/stripePriceOptions';
import { stripeConnectService } from '@/services/stripeConnect.service';

type LoadState = 'loading' | 'notConnected' | 'ready' | 'error';

const UNTIL_CANCELED = 'untilCanceled';
const INSTALLMENT_COUNTS = Array.from(
  { length: INSTALLMENT_COUNT_MAX - INSTALLMENT_COUNT_MIN + 1 },
  (_, index) => INSTALLMENT_COUNT_MIN + index,
);

interface StripePricesFieldProps {
  workspaceId?: number;
  value: StripePriceOptionInput[];
  onChange: (options: StripePriceOptionInput[]) => void;
  disabled?: boolean;
  // Prix archivé ou compte déconnecté depuis l'enregistrement de l'offre.
  unavailable?: boolean;
  error?: string;
}

export default function StripePricesField(props: StripePricesFieldProps) {
  const [reloadKey, setReloadKey] = useState(0);
  return (
    <StripePricesSelect
      key={reloadKey}
      {...props}
      onReload={() => setReloadKey((previous) => previous + 1)}
    />
  );
}

function StripePricesSelect({
  workspaceId,
  value,
  onChange,
  disabled,
  unavailable,
  error,
  onReload,
}: StripePricesFieldProps & { onReload: () => void }) {
  const { t, i18n } = useTranslation('createWebinar');
  const [state, setState] = useState<LoadState>('loading');
  const [prices, setPrices] = useState<StripeConnectedPrice[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!workspaceId) throw new Error('workspace');
      const account = await stripeConnectService.getAccount(workspaceId);
      if (!account.connected) return 'notConnected' as const;
      const page = await stripeConnectService.listPrices(workspaceId);
      if (cancelled) return null;
      setPrices(page.items);
      setNextCursor(page.hasMore ? page.nextCursor : null);
      return 'ready' as const;
    };
    load()
      .then((next) => {
        if (!cancelled && next) setState(next);
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const handleLoadMore = async () => {
    if (!workspaceId || !nextCursor) return;
    setIsLoadingMore(true);
    try {
      const page = await stripeConnectService.listPrices(workspaceId, nextCursor);
      setPrices((current) => mergeStripePrices(current, page.items));
      setNextCursor(page.hasMore ? page.nextCursor : null);
    } catch {
      setState('error');
    } finally {
      setIsLoadingMore(false);
    }
  };

  const priceLabel = (price: StripeConnectedPrice) =>
    formatStripeConnectedPriceLabel(price, i18n.language, (interval, count) =>
      t(`offersForm.content.stripeInterval.${interval}`, { count }),
    );

  if (state === 'loading') {
    return (
      <div className="flex items-center gap-2 text-sm text-secondary">
        <Loader />
        {t('offersForm.content.stripePricesLoading')}
      </div>
    );
  }

  if (state === 'notConnected') {
    return (
      <p className="flex flex-wrap items-center gap-1 text-sm text-secondary">
        <CircleAlert size={16} /> {t('offersForm.content.stripeNotConnected')}{' '}
        <Link to="/integrations" className="text-primary underline underline-offset-2">
          {t('offersForm.content.stripeConnectLink')}
        </Link>
      </p>
    );
  }

  if (state === 'error') {
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm text-red-500">
        {t('offersForm.content.stripePricesError')}
        <button
          type="button"
          onClick={onReload}
          className="flex items-center gap-1 rounded-lg border border-primary/20 px-2 py-1 text-xs text-primary cursor-pointer hover:bg-primary/10"
        >
          <RotateCw size={12} aria-hidden /> {t('offersForm.content.stripePricesRetry')}
        </button>
      </div>
    );
  }

  const rows = value.length ? value : [{ priceId: '', installmentCount: null }];

  const updateRow = (index: number, next: StripePriceOptionInput) =>
    onChange(rows.map((row, rowIndex) => (rowIndex === index ? next : row)));

  // Un prix en une fois n'a pas d'échéances : on les retire si l'admin change de prix.
  const handlePriceChange = (index: number, priceId: string) => {
    const recurring = prices.find((price) => price.id === priceId)?.recurringInterval != null;
    updateRow(index, {
      priceId,
      installmentCount: recurring ? rows[index].installmentCount : null,
    });
  };

  return (
    <div className="flex w-full flex-col gap-2">
      {rows.map((row, index) => {
        const knownPrice = prices.find((price) => price.id === row.priceId);
        const isUnknownValue = !!row.priceId && !knownPrice;
        const showInstallments =
          knownPrice?.recurringInterval != null || (isUnknownValue && row.installmentCount != null);
        const usedElsewhere = new Set(
          rows.filter((_, rowIndex) => rowIndex !== index).map((other) => other.priceId),
        );
        return (
          <div key={index} className="flex w-full flex-wrap items-start gap-2 sm:flex-nowrap">
            <div className="min-w-0 flex-1">
              <Select
                value={row.priceId || undefined}
                onValueChange={(priceId) => handlePriceChange(index, priceId)}
                disabled={disabled}
              >
                <SelectTrigger
                  label={
                    rows.length > 1
                      ? t('offersForm.content.stripeOptionLabel', { index: index + 1 })
                      : t('offersForm.content.stripePriceLabel')
                  }
                >
                  <SelectValue placeholder={t('offersForm.content.stripePricePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {isUnknownValue && (
                    <SelectItem value={row.priceId}>
                      {t('offersForm.content.stripePriceCurrent', { id: row.priceId })}
                    </SelectItem>
                  )}
                  {prices
                    .filter((price) => !usedElsewhere.has(price.id))
                    .map((price) => (
                      <SelectItem key={price.id} value={price.id}>
                        {priceLabel(price)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            {showInstallments && (
              <div className="w-full sm:w-56 sm:shrink-0">
                <Select
                  value={
                    row.installmentCount == null ? UNTIL_CANCELED : String(row.installmentCount)
                  }
                  onValueChange={(next) =>
                    updateRow(index, {
                      ...row,
                      installmentCount: next === UNTIL_CANCELED ? null : Number(next),
                    })
                  }
                  disabled={disabled}
                >
                  <SelectTrigger label={t('offersForm.content.stripeInstallmentsLabel')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNTIL_CANCELED}>
                      {t('offersForm.content.stripeInstallmentsUntilCanceled')}
                    </SelectItem>
                    {INSTALLMENT_COUNTS.map((count) => (
                      <SelectItem key={count} value={String(count)}>
                        {t('offersForm.content.stripeInstallmentsCount', { count })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {rows.length > 1 && (
              <button
                type="button"
                onClick={() => onChange(rows.filter((_, rowIndex) => rowIndex !== index))}
                disabled={disabled}
                aria-label={t('offersForm.content.stripeRemoveOption', { index: index + 1 })}
                title={t('offersForm.content.stripeRemoveOption', { index: index + 1 })}
                className="mt-2 shrink-0 rounded-lg p-2 text-secondary cursor-pointer hover:bg-primary/10 hover:text-red-500 disabled:opacity-50"
              >
                <Trash2 size={16} aria-hidden />
              </button>
            )}
          </div>
        );
      })}
      {/* Le back filtre les prix incompatibles : une page peut être vide sans être la dernière. */}
      {prices.length === 0 && !nextCursor && (
        <p className="text-xs text-secondary px-1">{t('offersForm.content.stripeNoPrices')}</p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        {rows.length < STRIPE_PRICE_OPTIONS_MAX && (prices.length > rows.length || nextCursor) && (
          <button
            type="button"
            onClick={() => onChange([...rows, { priceId: '', installmentCount: null }])}
            disabled={disabled}
            className="flex items-center gap-1 text-xs text-primary cursor-pointer disabled:opacity-50"
          >
            <Plus size={14} aria-hidden /> {t('offersForm.content.stripeAddOption')}
          </button>
        )}
        {nextCursor && (
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            className="text-xs text-primary underline underline-offset-2 cursor-pointer disabled:opacity-50"
          >
            {t('offersForm.content.stripePricesMore')}
          </button>
        )}
      </div>
      {unavailable && (
        <p className="flex items-center gap-1 text-xs text-red-500 px-1">
          <CircleAlert size={14} /> {t('offersForm.content.stripePaymentUnavailable')}
        </p>
      )}
      {error && <p className="text-left text-red-500 text-sm">{error}</p>}
      <p className="text-xs text-tertiary px-1">
        {t(
          rows.length > 1
            ? 'offersForm.content.stripeOptionsHint'
            : 'offersForm.content.stripePriceHint',
        )}
      </p>
    </div>
  );
}
